/**
 * Main Application Class
 *
 * Orchestrates all modules and components.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Modules
import SmoothScroll from '../modules/SmoothScroll.js';
import Header from '../modules/Header.js';
import MobileMenu from '../modules/MobileMenu.js';
import CustomScrollbar from '../modules/CustomScrollbar.js';
import PropertyGallery from '../modules/PropertyGallery.js';
import PropertyStickyBar from '../modules/PropertyStickyBar.js';
import PropertyFilters from '../modules/PropertyFilters.js';
import InquiryModal from '../modules/InquiryModal.js';
import PropertyMap from '../modules/PropertyMap.js';

// Animations
import ScrollAnimations from '../animations/ScrollAnimations.js';
import HeroAnimation from '../animations/HeroAnimation.js';
import HeroShowcase from '../animations/HeroShowcase.js';

// Components
import Carousel from '../components/Carousel.js';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default class App {
  constructor() {
    this.modules = {};
    this.isReady = false;
  }

  /**
   * Initialize the application
   */
  init() {
    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Initialize modules
    this.initModules();

    // Wait for fonts to load before animations
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.initAnimations();
        this.isReady = true;
        this.onReady();
      });
    } else {
      // Fallback for browsers without Font Loading API
      this.initAnimations();
      this.isReady = true;
      this.onReady();
    }

    // Initialize components
    this.initComponents();
  }

  /**
   * Initialize core modules
   */
  initModules() {
    // Smooth scroll (must initialize first for GSAP sync)
    if (!this.prefersReducedMotion) {
      this.modules.smoothScroll = new SmoothScroll();
    }

    // Custom scrollbar (overlays on content)
    const lenis = this.modules.smoothScroll?.lenis || null;
    this.modules.customScrollbar = new CustomScrollbar(lenis);

    // Header scroll behavior
    this.modules.header = new Header();

    // Mobile menu
    this.modules.mobileMenu = new MobileMenu();

    // Property-specific modules (conditional)
    if (document.querySelector('[data-property-gallery]')) {
      this.modules.propertyGallery = new PropertyGallery();
      this.modules.propertyStickyBar = new PropertyStickyBar();
      this.modules.inquiryModal = new InquiryModal();
    }

    // Property map (OpenStreetMap)
    if (document.getElementById('property-map')) {
      this.modules.propertyMap = new PropertyMap();
    }

    if (document.querySelector('[data-filter-panel]') || document.querySelector('[data-property-filters]')) {
      this.modules.propertyFilters = new PropertyFilters();
    }
  }

  /**
   * Initialize animations
   */
  initAnimations() {
    if (this.prefersReducedMotion) {
      // Show all elements immediately if reduced motion preferred
      gsap.set('[data-animate], [data-stagger-item]', {
        opacity: 1,
        transform: 'none',
      });
      return;
    }

    // Scroll-triggered animations
    this.modules.scrollAnimations = new ScrollAnimations();

    // Hero animation
    this.modules.heroAnimation = new HeroAnimation();

    // Hero showcase slider
    this.modules.heroShowcase = new HeroShowcase();
  }

  /**
   * Initialize UI components
   */
  initComponents() {
    // Testimonial carousel
    const carouselEl = document.querySelector('[data-carousel]');
    if (carouselEl) {
      this.modules.carousel = new Carousel(carouselEl);
    }
  }

  /**
   * Called when app is fully ready
   */
  onReady() {
    // Add ready class to body
    document.body.classList.add('is-ready');

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('tre:ready', { detail: { app: this } }));

    console.log('TheRealEstates theme initialized');
  }

  /**
   * Get Lenis instance for external use
   */
  getLenis() {
    return this.modules.smoothScroll?.lenis || null;
  }

  /**
   * Scroll to element
   */
  scrollTo(target, options = {}) {
    if (this.modules.smoothScroll) {
      this.modules.smoothScroll.scrollTo(target, options);
    } else {
      // Fallback for reduced motion
      const element = typeof target === 'string' ? document.querySelector(target) : target;
      if (element) {
        element.scrollIntoView({ behavior: 'auto', ...options });
      }
    }
  }

  /**
   * Refresh ScrollTrigger (call after dynamic content changes)
   */
  refresh() {
    ScrollTrigger.refresh();
  }

  /**
   * Destroy all modules
   */
  destroy() {
    Object.values(this.modules).forEach((module) => {
      if (typeof module.destroy === 'function') {
        module.destroy();
      }
    });

    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    this.modules = {};
    this.isReady = false;
  }
}
